/**
 * Eligibility guidance copy (question context lines, suggested tips and
 * per-option tips) is stored as free text that packs several labelled parts
 * into one string, separated by newlines and sometimes a bullet glyph:
 *
 *   "Why it matters: ...\n•Tip: Examples include the State Department, UN, ..."
 *
 * Rendered straight into a <p> the newlines collapse, so the parts run
 * together and the bullet ends up mid-sentence. Splitting the string into
 * labelled sections lets the UI lay each part out on its own.
 */
export type GuidanceSection = {
  /** Normalised display label, or null for a plain unlabelled sentence. */
  label: string | null;
  body: string;
};

/**
 * Labels we recognise, lower-cased. Only these are treated as labels, so an
 * ordinary sentence that happens to contain a colon is left alone.
 */
const KNOWN_LABELS: Record<string, string> = {
  "what it means": "What it means",
  "why it matters": "Why it matters",
  // Content typo on N-400 question 21; render the label that was intended.
  "what it matters": "Why it matters",
  tip: "Tip",
  tips: "Tip",
  note: "Note",
  example: "For example",
  examples: "For example",
  "for example": "For example",
};

const LABEL_PATTERN = /^([^:]{2,40}):\s*([\s\S]+)$/;

export const parseGuidanceSections = (
  raw?: string | null
): GuidanceSection[] => {
  if (!raw) return [];

  return raw
    // Bullet glyphs are used as separators rather than list markers.
    .replace(/[•·]/g, "\n")
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*[*-]\s+/, "").trim())
    .filter(Boolean)
    .map((line) => {
      const match = line.match(LABEL_PATTERN);
      if (match) {
        const label = KNOWN_LABELS[match[1].trim().toLowerCase()];
        if (label) {
          return { label, body: match[2].trim() };
        }
      }
      return { label: null, body: line };
    });
};
